import { useState, useEffect } from "react";
import Header from "./Components/Header";
import Menu from "./Components/Menu";
import { config } from "./Components/Config";
import { Modal } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import * as XLSX from 'xlsx';

const Invitation = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // State declarations
    const [invitations, setInvitations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isActionLoading, setIsActionLoading] = useState(false);

    const [tableSearch, setTableSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
    });

    const [showModal, setShowModal] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [showResendConfirm, setShowResendConfirm] = useState(false);
    const [showInviteConfirm, setShowInviteConfirm] = useState(false);
    const [invites, setInvites] = useState([{ name: "", email: "" }]);
    const [selectedResend, setSelectedResend] = useState([]);
    const [duplicateEmails, setDuplicateEmails] = useState([]);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(tableSearch);
        }, 500);
        return () => clearTimeout(timer);
    }, [tableSearch]);

    // Fetch invitations (Mock logic or use real endpoint if exists)
    useEffect(() => {
        fetchInvitations();
    }, [pagination.page, pagination.limit, debouncedSearch]);

    const fetchInvitations = async () => {
        setIsLoading(true);
        try {
            // Note: Update this endpoint if a real one exists for fetching invitations
            const queryParams = new URLSearchParams({
                page: pagination.page,
                limit: pagination.limit,
                ...(debouncedSearch && { search: debouncedSearch })
            });

            const response = await fetch(`${config.EVENT_ENDPOINT}invitation/${id}?${queryParams.toString()}`);

            if (response.ok) {
                const result = await response.json();
                setInvitations(result.data || []);
                setPagination(prev => ({
                    ...prev,
                    total: result.meta?.total || 0,
                    totalPages: result.meta?.totalPages || 0
                }));
            } else {
                // Mock empty state if endpoint is not fully ready
                setInvitations([]);
                setPagination(prev => ({ ...prev, total: 0, totalPages: 0 }));
            }
        } catch (error) {
            console.error("Error fetching invitations:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const clearSearch = () => {
        setTableSearch('');
    };

    const handleDownloadTemplate = () => {
        const templateData = [
            { name: "John Doe", email: "johndoe@example.com" },
            { name: "Jane Smith", email: "janesmith@example.com" }
        ];

        const worksheet = XLSX.utils.json_to_sheet(templateData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
        XLSX.writeFile(workbook, "template_invitation.xlsx");
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const data = new Uint8Array(event.target.result);
            const workbook = XLSX.read(data, { type: 'array' });

            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];

            const json = XLSX.utils.sheet_to_json(worksheet);

            const importedInvites = json.map((row) => ({
                name: row.Name || row.name || row.NAME || "",
                email: row.Email || row.email || row.EMAIL || ""
            })).filter(inv => inv.name || inv.email); // Buang baris yang benar-benar kosong

            if (importedInvites.length > 0) {
                const existingValidInvites = invites.filter(inv => inv.name.trim() || inv.email.trim());
                setInvites([...existingValidInvites, ...importedInvites]);
            }
        };

        reader.readAsArrayBuffer(file);
        e.target.value = null;
    };

    const handleCloseModal = () => {
        setShowCancelConfirm(true);
    };

    const confirmCloseModal = () => {
        setShowCancelConfirm(false);
        setShowModal(false);
        setInvites([{ name: "", email: "" }]);
        setDuplicateEmails([]);
    };

    const cancelCloseModal = () => {
        setShowCancelConfirm(false);
    };

    const handlePreSendInvitation = () => {
        const validInvites = invites.filter(inv => inv.name.trim() && inv.email.trim());
        if (validInvites.length > 0) {
            setShowInviteConfirm(true);
        }
    };

    const handleSendInvitation = async () => {
        const validInvites = invites.filter(inv => inv.name.trim() && inv.email.trim());
        if (validInvites.length === 0) return;

        setIsActionLoading(true);
        setDuplicateEmails([]);
        try {
            const response = await fetch(`${config.EVENT_ENDPOINT}invitation/${id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(validInvites)
            });

            if (response.ok) {
                setShowInviteConfirm(false);
                setShowModal(false);
                setInvites([{ name: "", email: "" }]);
                fetchInvitations(); // Refresh tabel
            } else {
                const result = await response.json();
                if (result.details && result.details.duplicates) {
                    setDuplicateEmails(result.details.duplicates);
                    setShowInviteConfirm(false); // Close confirm modal to show error in main modal
                } else {
                    console.error("Failed to send invitations", result.error || "Unknown error");
                }
            }
        } catch (error) {
            console.error("Error sending invitations:", error);
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleAddInvite = () => {
        setInvites([...invites, { name: "", email: "" }]);
    };

    const handleRemoveInvite = (index) => {
        const newInvites = [...invites];
        newInvites.splice(index, 1);
        setInvites(newInvites);
    };

    const handleInviteChange = (index, field, value) => {
        const newInvites = [...invites];
        newInvites[index][field] = value;
        setInvites(newInvites);
    };

    const needResend = invitations.filter(inv => inv.status === 0 || inv.status === null);
    const isAllSelected = needResend.length > 0 && selectedResend.length === needResend.length;

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedResend(needResend.map(inv => inv.id));
        } else {
            setSelectedResend([]);
        }
    };

    const handleSelectOne = (e, id) => {
        if (e.target.checked) {
            setSelectedResend([...selectedResend, id]);
        } else {
            setSelectedResend(selectedResend.filter(item => item !== id));
        }
    };

    const handlePreResendSelected = () => {
        if (selectedResend.length > 0) {
            setShowResendConfirm(true);
        }
    };

    const handleResendSelected = async () => {
        if (selectedResend.length === 0) return;
        setIsActionLoading(true);

        const payload = invitations
            .filter(inv => selectedResend.includes(inv.id))
            .map(inv => ({ name: inv.name, email: inv.email }));

        try {
            const response = await fetch(`${config.EVENT_ENDPOINT}invitation/${id}/resend`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                setShowResendConfirm(false);
                setSelectedResend([]);
                fetchInvitations();
            } else {
                console.error("Failed to resend");
            }
        } catch (error) {
            console.error("Error resend:", error);
        } finally {
            setIsActionLoading(false);
        }
    };

    return (
        <div className="d-flex">
            <div className="container-fluid p-0">
                <div className="row g-0 mh-100vh">
                    <div className="col-md-2 border-end">
                        <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
                            <a className="h-100" href="/dashboard"><img className="h-100" src="/cuancuan.png" alt="PointStar" /></a>
                        </div>
                        <hr className="m-0" />
                        <Menu active="event" />
                    </div>
                    <div className="col-md-10">
                        <Header />
                        <hr className="m-0" />
                        <div className="main">
                            <div className="p-4">
                                <div className="d-flex">
                                    <div className="d-flex align-items-center text-secondary mb-4">
                                        <span className="cursor-pointer hover-text-dark" onClick={() => navigate('/event')} style={{ cursor: 'pointer' }}>Event</span>
                                        <i className="bi bi-chevron-right mx-2 fs-6"></i>
                                        <span className="text-dark">Invitation Detail</span>
                                    </div>

                                </div>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h3 className="mb-4 fw-bold">Invitations</h3>
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <div className="d-flex align-items-center justify-content-between gap-2">
                                            <div className="input-group input-search">
                                                <button className="btn bg-white" type="button" onClick={() => clearSearch()}>
                                                    {tableSearch ? <i className="bi bi-x-lg" /> : <i className="bi bi-search" />}
                                                </button>
                                                <input
                                                    className="form-control"
                                                    type="text"
                                                    placeholder="Search"
                                                    value={tableSearch}
                                                    onChange={(e) => setTableSearch(e.target.value)}
                                                />
                                            </div>
                                            {selectedResend.length > 0 && (
                                                <button
                                                    className="btn btn-warning text-dark border-0"
                                                    type="button"
                                                    onClick={handlePreResendSelected}
                                                    disabled={isActionLoading}
                                                >
                                                    <i className="bi bi-arrow-repeat me-1" />
                                                    Resend ({selectedResend.length})
                                                </button>
                                            )}
                                            <button className="btn btn-primary bg-gold text-dark border-0" type="button" onClick={() => setShowModal(true)}>
                                                <i className="bi bi-plus-circle me-1" /> Invite
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white rounded-3 shadow-sm p-4">
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle">
                                            <thead>
                                                <tr>
                                                    <th className="bg-gold border-0 text-center" style={{ width: '4%', borderTopLeftRadius: '8px' }}>
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={isAllSelected}
                                                            onChange={handleSelectAll}
                                                            disabled={needResend.length === 0}
                                                        />
                                                    </th>
                                                    <th className="bg-gold fw-bold border-0 text-center" style={{ width: '5%' }}>#</th>
                                                    <th className="bg-gold fw-bold border-0 text-start">Name</th>
                                                    <th className="bg-gold fw-bold border-0 text-start">Email</th>
                                                    <th className="bg-gold fw-bold border-0 text-start">Status</th>
                                                    <th className="bg-gold fw-bold border-0 text-center" style={{ borderTopRightRadius: '8px' }}>Last Sent Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {isLoading ? (
                                                    <tr>
                                                        <td colSpan="6" className="text-center py-4 text-secondary">
                                                            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                                            Loading data...
                                                        </td>
                                                    </tr>
                                                ) : invitations.length > 0 ? (
                                                    invitations.map((inv, idx) => (
                                                        <tr key={inv.id || idx}>
                                                            <td className="text-center">
                                                                {(inv.status === 0 || inv.status === null) && (
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input"
                                                                        checked={selectedResend.includes(inv.id)}
                                                                        onChange={(e) => handleSelectOne(e, inv.id)}
                                                                    />
                                                                )}
                                                            </td>
                                                            <td className="text-center">{((pagination.page - 1) * pagination.limit) + idx + 1}</td>
                                                            <td>{inv.name}</td>
                                                            <td>{inv.email}</td>
                                                            <td>
                                                                {inv.status === 2 ? 'Sent' :
                                                                    inv.status === 1 ? 'Sending' :
                                                                        inv.status === 0 ? 'Failed' :
                                                                            'Not Sent'}
                                                            </td>
                                                            <td className="text-center text-secondary">
                                                                {inv.sendingAt ? format(new Date(inv.sendingAt), 'd MMM yyyy HH:mm') : '-'}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="6" className="text-center py-4 text-secondary">No invitations found</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    {pagination.totalPages > 0 && (
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <div className="d-flex align-items-center gap-2 text-secondary">
                                                <span>Rows per page:</span>
                                                <select
                                                    className="form-select form-select-sm border-0 bg-light"
                                                    style={{ width: '70px', cursor: 'pointer' }}
                                                    value={pagination.limit}
                                                    onChange={(e) => setPagination(prev => ({ ...prev, limit: Number(e.target.value), page: 1 }))}
                                                >
                                                    <option value={10}>10</option>
                                                    <option value={20}>20</option>
                                                    <option value={50}>50</option>
                                                </select>
                                            </div>
                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn btn-sm btn-light"
                                                    disabled={pagination.page === 1}
                                                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                                >
                                                    <i className="bi bi-chevron-left"></i>
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-light"
                                                    disabled={pagination.page >= pagination.totalPages}
                                                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                                >
                                                    <i className="bi bi-chevron-right"></i>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Invite Modal */}
            <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold fs-5">Send Invitation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {duplicateEmails.length > 0 && (
                        <div className="alert alert-danger d-flex flex-column mb-4 rounded-3 border-danger" role="alert">
                            <div className="d-flex align-items-center mb-2">
                                <i className="bi bi-exclamation-triangle-fill fs-5 me-2"></i>
                                <span className="fw-bold">Some emails are already invited</span>
                            </div>
                            <div className="ms-4">
                                <ul className="mb-0 ps-3">
                                    {/* Deduplicate array before render to avoid repetitive names */}
                                    {[...new Set(duplicateEmails)].map((email, idx) => (
                                        <li key={idx} className="small">{email}</li>
                                    ))}
                                </ul>
                                <small className="d-block mt-2 text-muted">Please remove or update these emails before sending.</small>
                            </div>
                        </div>
                    )}
                    <div className="mb-3">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <label className="form-label text-secondary fw-semibold align-items-center mb-0">
                                Participants <span className="text-danger">*</span>
                            </label>

                            <div className="d-flex gap-2">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary d-flex align-items-center gap-2"
                                    onClick={handleDownloadTemplate}
                                >
                                    <i className="bi bi-download"></i> Download Template
                                </button>

                                <div>
                                    <input
                                        type="file"
                                        id="excel-upload"
                                        accept=".xlsx, .xls, .csv"
                                        className="d-none"
                                        onChange={handleFileUpload}
                                    />
                                    <label
                                        htmlFor="excel-upload"
                                        className="btn btn-sm btn-outline-success d-flex align-items-center gap-2 cursor-pointer mb-0"
                                    >
                                        <i className="bi bi-upload"></i> Import Excel
                                    </label>
                                </div>
                            </div>
                        </div>

                        {invites.map((invite, index) => (
                            <div key={index} className="d-flex align-items-center gap-3 mb-3">
                                <div className="flex-grow-1">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Name"
                                        value={invite.name}
                                        onChange={(e) => handleInviteChange(index, "name", e.target.value)}
                                    />
                                </div>
                                <div className="flex-grow-1">
                                    <input
                                        type="email"
                                        className="form-control"
                                        placeholder="Email Address"
                                        value={invite.email}
                                        onChange={(e) => handleInviteChange(index, "email", e.target.value)}
                                    />
                                </div>
                                <div className="d-flex gap-2">
                                    {invites.length > 1 && (
                                        <button
                                            className="btn btn-outline-danger"
                                            onClick={() => handleRemoveInvite(index)}
                                            title="Remove"
                                        >
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    )}
                                    {index === invites.length - 1 && (
                                        <button
                                            className="btn btn-outline-primary"
                                            onClick={handleAddInvite}
                                            title="Add more"
                                        >
                                            <i className="bi bi-plus-lg"></i>
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                        <small className="text-muted mt-2 d-block">
                            Ensure both name and email are filled in correctly
                        </small>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <button className="btn btn-light fw-semibold" onClick={handleCloseModal} disabled={isActionLoading}>
                        Cancel
                    </button>
                    <button
                        className="btn bg-gold text-dark fw-semibold d-flex align-items-center justify-content-center gap-2"
                        onClick={handlePreSendInvitation}
                        disabled={invites.filter(inv => inv.name.trim() && inv.email.trim()).length === 0 || isActionLoading}
                    >
                        Send Invitation
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Invite Confirmation Modal */}
            <Modal show={showInviteConfirm} onHide={() => !isActionLoading && setShowInviteConfirm(false)} centered>
                <Modal.Header closeButton={!isActionLoading}>
                    <Modal.Title className="fs-5 fw-bold text-dark">Confirm Invitation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to send invitations to the selected participants?
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-light px-4 py-2 border fw-medium" onClick={() => setShowInviteConfirm(false)} disabled={isActionLoading}>
                        Cancel
                    </button>
                    <button className="btn bg-gold px-4 py-2 border-0 fw-medium text-dark" onClick={handleSendInvitation} disabled={isActionLoading}>
                        {isActionLoading ? (
                            <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> Sending...</>
                        ) : (
                            'Yes, Send'
                        )}
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Resend Confirmation Modal */}
            <Modal show={showResendConfirm} onHide={() => !isActionLoading && setShowResendConfirm(false)} centered>
                <Modal.Header closeButton={!isActionLoading}>
                    <Modal.Title className="fs-5 fw-bold text-dark">Confirm Resend</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to resend the invitation to {selectedResend.length} selected participant(s)?
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-light px-4 py-2 border fw-medium" onClick={() => setShowResendConfirm(false)} disabled={isActionLoading}>
                        Cancel
                    </button>
                    <button className="btn btn-warning px-4 py-2 border-0 fw-medium text-dark" onClick={handleResendSelected} disabled={isActionLoading}>
                        {isActionLoading ? (
                            <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> Resending...</>
                        ) : (
                            'Yes, Resend'
                        )}
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Cancel Confirmation Modal */}
            <Modal show={showCancelConfirm} onHide={cancelCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fs-5 fw-bold text-dark">Confirm Cancel</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to cancel? Any unsaved invitations will be lost.
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-light px-4 py-2 border fw-medium" onClick={cancelCloseModal}>
                        No, Keep Editing
                    </button>
                    <button className="btn btn-danger px-4 py-2 border-0 fw-medium text-white" onClick={confirmCloseModal}>
                        Yes, Cancel
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Invitation;



